import { useState, useEffect, useCallback, useRef } from "react";
import { useApp } from "../context/AppContext";

const PRIORITY_MAP = {
  low: { label: "Low", color: "#6b7280", bg: "rgba(107,114,128,0.1)" },
  medium: { label: "Medium", color: "#f59e0b", bg: "rgba(245,158,11,0.1)" },
  high: { label: "High", color: "#ef4444", bg: "rgba(239,68,68,0.1)" },
  urgent: { label: "Urgent", color: "#dc2626", bg: "rgba(220,38,38,0.15)" },
};

// Human labels for the journey-history status codes.
const HISTORY_STATUS_LABEL = {
  open: "Створено", take_to_work: "Взято в роботу", rework: "Доопрацювати",
  planned_3days: "Заплановано (3 дні)", in_work_today: "В роботі сьогодні",
  lacking_access: "Немає доступів", qa_rejected: "QA відхилив",
  post_impl_review: "Перевірка після впровадження", done: "Здано",
  sem_take: "Семантика: взято", sem_work: "Семантика: в роботі",
  sem_review: "Семантика: перевірка", sem_done: "Семантика: готово",
};

// Semantics status tag shown on cards sitting in the "Семантика (Sem)" column.
// The task never leaves that column — the tag reflects the semantist's stage instead.
const SEM_STATUS_BADGE = {
  sem_take:   { label: "📚 Взято семантистом", color: "#0ea5e9", bg: "rgba(14,165,233,0.12)" },
  sem_work:   { label: "📚 Семантика в роботі", color: "#3b82f6", bg: "rgba(59,130,246,0.12)" },
  sem_review: { label: "📚 Семантика на перевірці", color: "#06b6d4", bg: "rgba(6,182,212,0.12)" },
  sem_done:   { label: "✅ Семантика готова", color: "#22c55e", bg: "rgba(34,197,94,0.16)" },
};

// ТЗ-шаблон: фірмовий заголовок з логотипом + футер з логотипом і підписом агенції.
const BRIEF_TEMPLATE = `
<div style="text-align:center;padding:18px 14px;background:linear-gradient(135deg,rgba(99,102,241,0.08),rgba(168,85,247,0.06));border-radius:12px;border:1px solid rgba(99,102,241,0.18);margin-bottom:14px;">
  <img src="/svitlo-logo.png" alt="Svitlo Agency" style="height:48px;max-width:180px;object-fit:contain;display:block;margin:0 auto 8px;" />
  <div style="font-size:11px;color:#6b7280;letter-spacing:1.5px;text-transform:uppercase;font-weight:600;">Технічне завдання</div>
</div>
<h2>📌 Мета</h2>
<p><br></p>
<h2>📝 Контекст</h2>
<p><br></p>
<h2>✅ Що зробити</h2>
<ul><li><br></li></ul>
<h2>🎯 Очікуваний результат / DoD</h2>
<p><br></p>
<h2>🔗 Посилання та матеріали</h2>
<p><br></p>
<div style="text-align:center;padding:14px 12px;margin-top:18px;border-top:1px solid rgba(99,102,241,0.18);opacity:0.85;">
  <img src="/svitlo-logo.png" alt="Svitlo Agency" style="height:32px;max-width:140px;object-fit:contain;display:block;margin:0 auto 6px;opacity:0.85;" />
  <div style="font-size:10px;color:#888;letter-spacing:0.4px;">Svitlo Agency · згенеровано через Spalah · seo-platform</div>
</div>
`.trim();


export default function KanbanView() {
  const { apiFetch, flash, dark, t, card, inp, btnP, btnG, btnSm, currentUser, projects, activeId, proj, userRole, setView } = useApp();

  const INBOX_TITLE = "Нова задача";

  const [columns, setColumns] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateMenu, setShowCreateMenu] = useState(false);
  const [creatingMode, setCreatingMode] = useState(null); // null | "scratch" | "template"
  const [newTitle, setNewTitle] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [newSpecUrl, setNewSpecUrl] = useState("");
  const [newAssignee, setNewAssignee] = useState("");
  const [newPriority, setNewPriority] = useState("medium");
  const [newProject, setNewProject] = useState("");
  const [newExternalDev, setNewExternalDev] = useState(false);
  const [creatingSaving, setCreatingSaving] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [showTemplateLib, setShowTemplateLib] = useState(false);
  const [templateSearch, setTemplateSearch] = useState("");
  useEffect(() => {
    apiFetch(`/api/kanban-templates`).then(r => r.json()).then(d => Array.isArray(d) && setTemplates(d)).catch(() => {});
  }, [apiFetch]);
  const applyTemplate = (tpl) => {
    resetCreateForm();
    setNewTitle(tpl.title_prefix || "");
    setNewDesc(tpl.body_html || "");
    setNewPriority(tpl.default_priority || "medium");
    setShowTemplateLib(false);
    setCreatingMode("scratch");
  };
  const filteredTemplates = templates.filter(tpl => {
    if (!templateSearch.trim()) return true;
    const q = templateSearch.toLowerCase();
    return (tpl.name || "").toLowerCase().includes(q) || (tpl.description || "").toLowerCase().includes(q);
  });
  const [dragging, setDragging] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [filterUser, setFilterUser] = useState("");
  const [filterPriority, setFilterPriority] = useState("");

  // Modal state
  const [openTask, setOpenTask] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState("");
  const commentsEndRef = useRef(null);
  const [history, setHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);

  const load = useCallback(async () => {
    if (!activeId) { setLoading(false); return; }
    try {
      const [colsR, tasksR, usersR] = await Promise.all([
        apiFetch(`/api/kanban/columns?projectId=${activeId}`).then(r => r.json()),
        apiFetch(`/api/kanban/tasks?projectId=${activeId}`).then(r => r.json()),
        apiFetch("/api/kanban/users").then(r => r.json()),
      ]);
      if (Array.isArray(colsR)) setColumns(colsR);
      if (Array.isArray(tasksR)) setTasks(tasksR);
      if (Array.isArray(usersR)) setUsers(usersR);
    } catch (e) { console.error("Kanban load:", e); }
    setLoading(false);
  }, [apiFetch, activeId]);

  useEffect(() => { load(); }, [load]);

  // Load comments when task opens
  const loadComments = async (taskId) => {
    setCommentsLoading(true);
    try {
      const r = await apiFetch(`/api/kanban/tasks/${taskId}/comments`);
      const d = await r.json();
      if (Array.isArray(d)) setComments(d);
    } catch {}
    setCommentsLoading(false);
  };

  const loadHistory = async (taskId) => {
    setHistoryLoading(true);
    try {
      const r = await apiFetch(`/api/kanban/tasks/${taskId}/history`);
      const d = await r.json();
      if (Array.isArray(d)) setHistory(d);
    } catch {}
    setHistoryLoading(false);
  };

  const openTaskModal = (task) => {
    setOpenTask(task);
    setComments([]);
    setCommentText("");
    setEditingField(null);
    setHistory([]);
    setHistoryOpen(false);
    loadComments(task.id);
    loadHistory(task.id);
  };

  const closeModal = () => { setOpenTask(null); setComments([]); setEditingField(null); setHistory([]); setHistoryOpen(false); };

  const addComment = async () => {
    if (!commentText.trim() || !openTask) return;
    try {
      const r = await apiFetch(`/api/kanban/tasks/${openTask.id}/comments`, {
        method: "POST", body: JSON.stringify({ text: commentText.trim() }),
      });
      const c = await r.json();
      if (c.id) { setComments(prev => [...prev, c]); setCommentText(""); }
      setTimeout(() => commentsEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch {}
  };

  const deleteComment = async (id) => {
    setComments(prev => prev.filter(c => c.id !== id));
    try { await apiFetch(`/api/kanban/comments/${id}`, { method: "DELETE" }); } catch {}
  };

  const inboxColumn = columns.find(c => c.title === INBOX_TITLE) || columns[0] || null;

  const resetCreateForm = () => {
    setNewTitle(""); setNewDesc(""); setNewSpecUrl(""); setNewAssignee(""); setNewPriority("medium"); setNewProject(""); setNewExternalDev(false);
  };

  const closeCreate = () => {
    setCreatingMode(null);
    setShowCreateMenu(false);
    resetCreateForm();
  };

  const createTaskFromScratch = async () => {
    if (!newTitle.trim()) { flash("Введіть назву задачі"); return; }
    if (!activeId) { flash("Немає активного проекту"); return; }
    if (!inboxColumn) { flash("Не знайдено колонку «Нова задача»"); return; }
    setCreatingSaving(true);
    try {
      const r = await apiFetch("/api/kanban/tasks", {
        method: "POST",
        body: JSON.stringify({
          title: newTitle.trim(),
          description: newDesc.trim(),
          column_id: inboxColumn.id,
          assignee_id: newAssignee || null,
          priority: newPriority,
          project_id: activeId,
          external_dev: newExternalDev,
        }),
      });
      const task = await r.json();
      if (task.id) {
        setTasks(prev => [...prev, task]);
        // Save spec_url separately if provided (POST endpoint doesn't accept it)
        if (newSpecUrl.trim()) {
          try {
            await apiFetch(`/api/kanban/tasks/${task.id}`, {
              method: "PUT", body: JSON.stringify({ spec_url: newSpecUrl.trim() }),
            });
            setTasks(prev => prev.map(tk => tk.id === task.id ? { ...tk, spec_url: newSpecUrl.trim() } : tk));
          } catch {}
        }
        flash("✓ Задачу створено");
        closeCreate();
      } else flash(task.error || "Помилка");
    } catch (e) { flash("Помилка: " + e.message); }
    setCreatingSaving(false);
  };

  const moveTask = async (taskId, newColId) => {
    setTasks(prev => prev.map(tk => tk.id === taskId ? { ...tk, column_id: newColId } : tk));
    if (openTask?.id === taskId) setOpenTask(prev => prev ? { ...prev, column_id: newColId } : prev);
    try { await apiFetch(`/api/kanban/tasks/${taskId}`, { method: "PUT", body: JSON.stringify({ column_id: newColId }) }); } catch { load(); }
  };

  const updateTask = async (taskId, updates) => {
    try {
      const r = await apiFetch(`/api/kanban/tasks/${taskId}`, { method: "PUT", body: JSON.stringify(updates) });
      const updated = await r.json();
      if (updated.id) {
        setTasks(prev => prev.map(tk => tk.id === updated.id ? updated : tk));
        if (openTask?.id === updated.id) setOpenTask(updated);
      }
    } catch {}
  };

  const deleteTask = async (taskId) => {
    setTasks(prev => prev.filter(tk => tk.id !== taskId));
    if (openTask?.id === taskId) closeModal();
    try { await apiFetch(`/api/kanban/tasks/${taskId}`, { method: "DELETE" }); } catch {}
  };

  const saveField = (field) => {
    if (!openTask) return;
    updateTask(openTask.id, { [field]: editValue });
    setEditingField(null);
  };

  const onDragStart = (e, taskId) => { setDragging(taskId); e.dataTransfer.effectAllowed = "move"; };
  const onDragEnd = () => { setDragging(null); setDragOver(null); };
  const onDragOverCol = (e, colId) => { e.preventDefault(); setDragOver(colId); };
  const onDropCol = (e, colId) => { e.preventDefault(); if (dragging) moveTask(dragging, colId); setDragging(null); setDragOver(null); };

  const getColTasks = (colId) => {
    let filtered = tasks.filter(tk => tk.column_id === colId);
    if (filterUser) filtered = filtered.filter(tk => tk.assignee_id === parseInt(filterUser));
    if (filterPriority) filtered = filtered.filter(tk => tk.priority === filterPriority);
    return filtered;
  };

  const getUserName = (id) => { const u = users.find(x => x.id === id); return u ? (u.full_name || u.username.split("@")[0]) : "—"; };
  const displayName = (task, field) => task[field + "_full_name"] || task[field + "_name"]?.split("@")[0] || "—";
  const getProjectName = (id) => { const p = (projects || []).find(p => p.id === id); return p ? p.name : ""; };
  const getColName = (id) => columns.find(c => c.id === id)?.title || "";
  const ago = (date) => {
    if (!date) return "";
    const mins = Math.floor((Date.now() - new Date(date).getTime()) / 60000);
    if (mins < 60) return `${mins}хв`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}год`;
    return `${Math.floor(hrs / 24)}д`;
  };

  if (!activeId) return <div style={{ textAlign: "center", padding: 40, color: t.textMuted }}>Виберіть проект, щоб побачити канбан-доску</div>;
  if (loading) return <div style={{ textAlign: "center", padding: 40, color: t.textMuted }}>Завантаження...</div>;

  return (
    <div style={{ height: "calc(100vh - 120px)", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14, flexShrink: 0 }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>📋 Канбан · {proj?.name || ""}</h2>
        <span style={{ fontSize: 11, color: t.textMuted }}>{tasks.length} задач</span>
        <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
          <select value={filterUser} onChange={e => setFilterUser(e.target.value)} style={{ ...inp, width: 140, fontSize: 11, padding: "4px 8px" }}>
            <option value="">Всі виконавці</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.full_name || u.username.split("@")[0]}{u.position ? ` (${u.position})` : ""}</option>)}
          </select>
          <select value={filterPriority} onChange={e => setFilterPriority(e.target.value)} style={{ ...inp, width: 110, fontSize: 11, padding: "4px 8px" }}>
            <option value="">Всі пріоритети</option>
            {Object.entries(PRIORITY_MAP).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>
      </div>

      {/* Board */}
      <div style={{ display: "flex", gap: 14, flex: 1, overflowX: "auto", paddingBottom: 8 }}>
        {columns.map(col => {
          const colTasks = getColTasks(col.id);
          const isOver = dragOver === col.id;
          return (
            <div key={col.id} onDragOver={e => onDragOverCol(e, col.id)} onDragLeave={() => setDragOver(null)} onDrop={e => onDropCol(e, col.id)}
              style={{
                minWidth: 300, width: 300, flexShrink: 0, display: "flex", flexDirection: "column",
                background: isOver ? (dark ? "rgba(99,102,241,0.08)" : "rgba(99,102,241,0.04)") : (dark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.015)"),
                borderRadius: 14, padding: "12px 10px", border: `2px solid ${isOver ? col.color + "66" : "transparent"}`, transition: "all 0.2s",
              }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, padding: "0 6px", position: "relative" }}>
                <div style={{ width: 10, height: 10, borderRadius: 3, background: col.color }} />
                <span style={{ fontSize: 13, fontWeight: 700 }}>{col.title}</span>
                <span style={{ fontSize: 10, fontWeight: 700, padding: "1px 7px", borderRadius: 10, background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)", color: t.textMuted }}>{colTasks.length}</span>
                {col.title === INBOX_TITLE && (
                  <button
                    onClick={() => setShowCreateMenu(v => !v)}
                    style={{
                      marginLeft: "auto", background: col.color, border: "none",
                      width: 24, height: 24, borderRadius: 6, fontSize: 16, fontWeight: 700,
                      cursor: "pointer", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 1,
                    }}
                    title="Створити нову задачу"
                  >＋</button>
                )}
                {col.title === INBOX_TITLE && showCreateMenu && (
                  <>
                    <div onClick={() => setShowCreateMenu(false)} style={{ position: "fixed", inset: 0, zIndex: 998 }} />
                    <div style={{
                      position: "absolute", top: 28, right: 0, zIndex: 999,
                      minWidth: 220, padding: 6, borderRadius: 10,
                      background: dark ? "#1a1a24" : "#fff",
                      border: `1px solid ${t.border}`,
                      boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
                    }}>
                      <button
                        onClick={() => {
                          setShowCreateMenu(false);
                          if (userRole === "admin") {
                            setView && setView("task_templates");
                          } else {
                            setTemplateSearch("");
                            setShowTemplateLib(true);
                          }
                        }}
                        style={createMenuBtn(dark, t)}
                      >
                        📚 База знань {userRole === "admin"
                          ? <span style={{ fontSize: 9, color: t.textMuted, marginLeft: 6 }}>(керувати)</span>
                          : <span style={{ fontSize: 9, color: t.textMuted, marginLeft: 6 }}>({templates.length})</span>}
                      </button>
                      <button
                        onClick={() => { setShowCreateMenu(false); resetCreateForm(); setCreatingMode("scratch"); }}
                        style={createMenuBtn(dark, t)}
                      >
                        ✍️ Створити з нуля
                      </button>
                    </div>
                  </>
                )}
              </div>

              <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 8 }}>
                {/* old inline form removed — creation now happens via the dropdown above */}

                {colTasks.map(task => {
                  const pr = PRIORITY_MAP[task.priority] || PRIORITY_MAP.medium;
                  const commentCount = tasks === task ? comments.length : 0; // placeholder
                  return (
                    <div key={task.id} draggable onDragStart={e => onDragStart(e, task.id)} onDragEnd={onDragEnd}
                      onClick={() => openTaskModal(task)}
                      style={{
                        padding: "10px 12px", borderRadius: 10, cursor: "pointer",
                        background: dark ? "#1a1a24" : "#fff",
                        border: `1px solid ${dragging === task.id ? col.color : (dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)")}`,
                        boxShadow: dragging === task.id ? "0 4px 16px rgba(0,0,0,0.2)" : "0 1px 3px rgba(0,0,0,0.04)",
                        opacity: dragging === task.id ? 0.6 : 1, transition: "all 0.2s",
                      }}>
                      <div style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 6 }}>
                        <div style={{ width: 4, minHeight: 20, borderRadius: 2, background: pr.color, flexShrink: 0, marginTop: 2 }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.3 }}>{task.title}</div>
                          {task.description && (() => {
                            const plain = stripHtml(task.description);
                            return <div style={{ fontSize: 11, color: t.textMuted, lineHeight: 1.4, marginTop: 2 }}>{plain.slice(0, 60)}{plain.length > 60 ? "..." : ""}</div>;
                          })()}
                        </div>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                        <span style={{ fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 4, background: pr.bg, color: pr.color }}>{pr.label}</span>
                        {task.external_dev && <span title="Сторонні розробники — не на Dev-дошці наших розробників" style={{ fontSize: 9, fontWeight: 800, padding: "1px 6px", borderRadius: 4, background: "rgba(249,115,22,0.14)", color: "#f97316" }}>🌐 Сторонні</span>}
                        {SEM_STATUS_BADGE[task.status] && (() => {
                          const sb = SEM_STATUS_BADGE[task.status];
                          const who = task.sem_assignee_full_name || (task.sem_assignee_name ? task.sem_assignee_name.split("@")[0] : "");
                          return <span title={who ? `Семантист: ${who}` : sb.label} style={{ fontSize: 9, fontWeight: 800, padding: "1px 6px", borderRadius: 4, background: sb.bg, color: sb.color }}>{sb.label}</span>;
                        })()}
                        {task.qa_passed_at && <span title={`Пройшла QA · ${task.qa_passed_by_full_name || task.qa_passed_by_name || ""}`} style={{ fontSize: 9, fontWeight: 800, padding: "1px 6px", borderRadius: 4, background: "rgba(16,185,129,0.15)", color: "#10b981" }}>✓ QA</span>}
                        {task.dev_assignee_name && <span title={`Розробник: ${task.dev_assignee_full_name || task.dev_assignee_name}`} style={{ fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 4, background: "rgba(168,85,247,0.12)", color: "#a855f7" }}>👨‍💻 {(task.dev_assignee_full_name || task.dev_assignee_name).split("@")[0]}</span>}
                        {task.spec_url && <span style={{ fontSize: 9, color: "#6366f1" }}>📎</span>}
                        {task.dev_estimate && <span title={`Оцінка розробника: ${task.dev_estimate}`} style={{ fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 4, background: "#eef2ff", color: "#4f46e5" }}>⏱ {task.dev_estimate}</span>}
                        <div style={{ flex: 1 }} />
                        {task.assignee_name && <span style={{ fontSize: 10, color: t.textMuted }}>→ {displayName(task, "assignee")}</span>}
                        <span style={{ fontSize: 9, color: dark ? "#555" : "#bbb" }}>{ago(task.created_at)}</span>
                      </div>
                    </div>
                  );
                })}
                {colTasks.length === 0 && <div style={{ textAlign: "center", padding: "20px 10px", color: t.textMuted, fontSize: 12 }}>{col.title === INBOX_TITLE ? "Натисніть «+» щоб створити задачу" : "Перетягніть задачу сюди"}</div>}
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Task Modal ── */}
      {openTask && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={closeModal}>
          <div onClick={e => e.stopPropagation()} style={{
            width: 680, maxHeight: "85vh", display: "flex", flexDirection: "column",
            background: dark ? "#1a1a24" : "#fff", borderRadius: 16,
            border: `1px solid ${t.border}`, boxShadow: "0 20px 60px rgba(0,0,0,.4)",
            overflow: "hidden",
          }}>
            {/* Modal header */}
            <div style={{ padding: "20px 24px 14px", borderBottom: `1px solid ${t.border}`, flexShrink: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                <div style={{ width: 4, height: 24, borderRadius: 2, background: (PRIORITY_MAP[openTask.priority] || PRIORITY_MAP.medium).color }} />
                {editingField === "title" ? (
                  <input value={editValue} onChange={e => setEditValue(e.target.value)} autoFocus
                    onKeyDown={e => { if (e.key === "Enter") saveField("title"); if (e.key === "Escape") setEditingField(null); }}
                    onBlur={() => saveField("title")}
                    style={{ ...inp, fontSize: 18, fontWeight: 700, padding: "4px 8px", flex: 1 }} />
                ) : (
                  <h3 onClick={() => { setEditingField("title"); setEditValue(openTask.title); }} style={{ fontSize: 18, fontWeight: 700, margin: 0, cursor: "pointer", flex: 1 }}>{openTask.title}</h3>
                )}
                <button onClick={closeModal} style={{ background: "none", border: "none", fontSize: 18, cursor: "pointer", color: t.textMuted }}>✕</button>
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                {/* Column */}
                <select value={openTask.column_id} onChange={e => moveTask(openTask.id, parseInt(e.target.value))}
                  style={{ ...inp, width: 100, fontSize: 11, padding: "3px 6px" }}>
                  {columns.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
                </select>
                {/* Assignee */}
                <select value={openTask.assignee_id || ""} onChange={e => updateTask(openTask.id, { assignee_id: e.target.value ? parseInt(e.target.value) : null })}
                  style={{ ...inp, width: 130, fontSize: 11, padding: "3px 6px" }}>
                  <option value="">Виконавець</option>
                  {users.map(u => <option key={u.id} value={u.id}>{u.full_name || u.username.split("@")[0]}{u.position ? ` (${u.position})` : ""}</option>)}
                </select>
                {/* Priority */}
                <select value={openTask.priority} onChange={e => updateTask(openTask.id, { priority: e.target.value })}
                  style={{ ...inp, width: 90, fontSize: 11, padding: "3px 6px" }}>
                  {Object.entries(PRIORITY_MAP).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
                {/* External devs toggle */}
                <button
                  onClick={() => { const v = !openTask.external_dev; setOpenTask(prev => prev ? { ...prev, external_dev: v } : prev); updateTask(openTask.id, { external_dev: v }); }}
                  title="Якщо увімкнено — задача не потрапляє на Dev-дошку наших розробників"
                  style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 6, cursor: "pointer", fontFamily: "inherit", border: `1px solid ${openTask.external_dev ? "#f97316" : t.border}`, background: openTask.external_dev ? "rgba(249,115,22,0.12)" : "transparent", color: openTask.external_dev ? "#f97316" : t.textMuted }}>
                  🌐 Сторонні {openTask.external_dev ? "✓" : ""}
                </button>
                <div style={{ flex: 1 }} />
                <button onClick={() => { deleteTask(openTask.id); }} style={{ ...btnSm, color: "#ef4444", fontSize: 11 }}>Видалити</button>
              </div>
            </div>

            {/* Modal body */}
            <div style={{ flex: 1, overflowY: "auto", padding: "16px 24px" }}>
              {/* Spec URL */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>📎 Посилання на ТЗ</div>
                {editingField === "spec_url" ? (
                  <div style={{ display: "flex", gap: 6 }}>
                    <input value={editValue} onChange={e => setEditValue(e.target.value)} placeholder="https://docs.google.com/..."
                      autoFocus onKeyDown={e => { if (e.key === "Enter") saveField("spec_url"); if (e.key === "Escape") setEditingField(null); }}
                      style={{ ...inp, flex: 1, fontSize: 12, padding: "6px 10px" }} />
                    <button onClick={() => saveField("spec_url")} style={{ ...btnP, fontSize: 11, padding: "6px 10px" }}>Зберегти</button>
                    <button onClick={() => setEditingField(null)} style={{ ...btnG, fontSize: 11, padding: "6px 8px" }}>✕</button>
                  </div>
                ) : (
                  <div onClick={() => { setEditingField("spec_url"); setEditValue(openTask.spec_url || ""); }}
                    style={{ padding: "8px 12px", borderRadius: 8, cursor: "pointer", border: `1px dashed ${t.border}`, fontSize: 12, minHeight: 36, display: "flex", alignItems: "center" }}>
                    {openTask.spec_url ? (
                      <a href={openTask.spec_url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} style={{ color: "#6366f1", textDecoration: "none", wordBreak: "break-all" }}>{openTask.spec_url}</a>
                    ) : (
                      <span style={{ color: t.textMuted }}>Натисніть щоб додати посилання на ТЗ...</span>
                    )}
                  </div>
                )}
              </div>

              {/* Dev estimate */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>⏱ Оцінка (від розробника)</div>
                {editingField === "dev_estimate" ? (
                  <div style={{ display: "flex", gap: 6 }}>
                    <input value={editValue} onChange={e => setEditValue(e.target.value)} placeholder="напр. 4 год / 2 дні / 1 тиждень"
                      autoFocus onKeyDown={e => { if (e.key === "Enter") saveField("dev_estimate"); if (e.key === "Escape") setEditingField(null); }}
                      style={{ ...inp, flex: 1, fontSize: 12, padding: "6px 10px" }} />
                    <button onClick={() => saveField("dev_estimate")} style={{ ...btnP, fontSize: 11, padding: "6px 10px" }}>Зберегти</button>
                    <button onClick={() => setEditingField(null)} style={{ ...btnG, fontSize: 11, padding: "6px 8px" }}>✕</button>
                  </div>
                ) : (
                  <div onClick={() => { setEditingField("dev_estimate"); setEditValue(openTask.dev_estimate || ""); }}
                    style={{ padding: "8px 12px", borderRadius: 8, cursor: "pointer", border: `1px dashed ${t.border}`, fontSize: 12, minHeight: 36, display: "flex", alignItems: "center" }}>
                    {openTask.dev_estimate ? <span style={{ fontWeight: 700 }}>{openTask.dev_estimate}</span> : <span style={{ color: t.textMuted }}>Натисніть щоб додати оцінку...</span>}
                  </div>
                )}
              </div>

              {/* Description */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>📝 Опис</div>
                {editingField === "description" ? (
                  <div>
                    <RichEditor value={editValue} onChange={setEditValue} dark={dark} t={t} />
                    <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                      <button onClick={() => saveField("description")} style={{ ...btnP, fontSize: 11, padding: "5px 12px" }}>Зберегти</button>
                      <button onClick={() => setEditingField(null)} style={{ ...btnG, fontSize: 11, padding: "5px 8px" }}>✕</button>
                    </div>
                  </div>
                ) : (
                  <div onClick={() => { setEditingField("description"); setEditValue(openTask.description || ""); }}
                    style={{ padding: "10px 12px", borderRadius: 8, cursor: "pointer", border: `1px dashed ${t.border}`, fontSize: 13, lineHeight: 1.6, minHeight: 50 }}>
                    {openTask.description
                      ? (/<[a-z][^>]*>/i.test(openTask.description)
                          ? <div className="rich-desc" dangerouslySetInnerHTML={{ __html: openTask.description }} />
                          : <div style={{ whiteSpace: "pre-wrap" }}>{openTask.description}</div>)
                      : <span style={{ color: t.textMuted }}>Натисніть щоб додати опис...</span>}
                  </div>
                )}
              </div>

              {/* Meta info */}
              <div style={{ display: "flex", gap: 16, fontSize: 11, color: t.textMuted, marginBottom: 20, padding: "10px 12px", borderRadius: 8, background: dark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.015)" }}>
                <span>Автор: <strong style={{ color: t.text }}>{displayName(openTask, "author")}</strong></span>
                <span>Створено: <strong style={{ color: t.text }}>{new Date(openTask.created_at).toLocaleString("uk-UA")}</strong></span>
                {openTask.updated_at !== openTask.created_at && <span>Оновлено: <strong style={{ color: t.text }}>{ago(openTask.updated_at)} тому</strong></span>}
              </div>

              {/* Journey history — collapsible timeline of column/status transitions */}
              <div style={{ marginBottom: 20, border: `1px solid ${t.border}`, borderRadius: 10, overflow: "hidden" }}>
                <button
                  onClick={() => setHistoryOpen(o => !o)}
                  style={{ width: "100%", display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", background: dark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.015)", border: "none", cursor: "pointer", color: t.text, fontFamily: "inherit", fontSize: 12, fontWeight: 700, textAlign: "left" }}>
                  <span style={{ transform: historyOpen ? "rotate(90deg)" : "none", transition: "transform .15s", color: t.textMuted }}>▶</span>
                  <span>🕘 Шлях задачі</span>
                  <span style={{ fontSize: 10, fontWeight: 700, color: t.textMuted, background: dark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)", padding: "1px 7px", borderRadius: 10 }}>{history.length}</span>
                </button>
                {historyOpen && (
                  <div style={{ padding: "8px 14px 12px", borderTop: `1px solid ${t.border}` }}>
                    {historyLoading && <div style={{ fontSize: 12, color: t.textMuted, padding: 6 }}>Завантаження...</div>}
                    {!historyLoading && history.length === 0 && <div style={{ fontSize: 12, color: t.textMuted, fontStyle: "italic", padding: 6 }}>Історія переміщень порожня (задача створена до ввімкнення історії).</div>}
                    {history.map((h, i) => {
                      const who = h.actor_full_name || (h.actor_name ? h.actor_name.split("@")[0] : "—");
                      const isLast = i === history.length - 1;
                      return (
                        <div key={h.id} style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "5px 0" }}>
                          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", alignSelf: "stretch" }}>
                            <span style={{ width: 9, height: 9, borderRadius: "50%", background: isLast ? "#10b981" : "#6366f1", flexShrink: 0, marginTop: 4 }} />
                            {!isLast && <span style={{ width: 2, flex: 1, background: t.border, marginTop: 2 }} />}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 12.5, fontWeight: 600, color: t.text }}>
                              → {h.to_column || "—"}
                              {h.status && HISTORY_STATUS_LABEL[h.status] && <span style={{ fontSize: 10.5, color: t.textMuted, fontWeight: 500 }}> · {HISTORY_STATUS_LABEL[h.status]}</span>}
                            </div>
                            <div style={{ fontSize: 10.5, color: t.textMuted, marginTop: 1 }}>{who} · {new Date(h.created_at).toLocaleString("uk-UA")}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Comments */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, marginBottom: 10, textTransform: "uppercase", letterSpacing: 0.5 }}>💬 Коментарі ({comments.length})</div>

                {commentsLoading && <div style={{ fontSize: 12, color: t.textMuted, padding: 10 }}>Завантаження...</div>}

                <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 12 }}>
                  {comments.map(c => (
                    <div key={c.id} style={{
                      padding: "10px 14px", borderRadius: 10,
                      background: dark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
                      border: `1px solid ${dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)"}`,
                    }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: "#6366f1" }}>{c.author_full_name || c.author_name?.split("@")[0] || "—"}</span>
                        <span style={{ fontSize: 10, color: t.textMuted }}>{new Date(c.created_at).toLocaleString("uk-UA")}</span>
                        <button onClick={() => deleteComment(c.id)} style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer", fontSize: 11, color: dark ? "#555" : "#ccc" }}>✕</button>
                      </div>
                      <div style={{ fontSize: 13, lineHeight: 1.5, whiteSpace: "pre-wrap" }}>{c.text}</div>
                    </div>
                  ))}
                  <div ref={commentsEndRef} />
                </div>

                {/* Add comment */}
                <div style={{ display: "flex", gap: 8 }}>
                  <textarea value={commentText} onChange={e => setCommentText(e.target.value)}
                    placeholder="Написати коментар..."
                    onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); addComment(); } }}
                    rows={2} style={{ ...inp, flex: 1, fontSize: 12, padding: "8px 10px", resize: "none" }} />
                  <button onClick={addComment} disabled={!commentText.trim()} style={{ ...btnP, fontSize: 11, padding: "8px 14px", alignSelf: "flex-end", opacity: commentText.trim() ? 1 : 0.5 }}>Надіслати</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Template library popup */}
      {showTemplateLib && (
        <div onClick={() => setShowTemplateLib(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 1400, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: dark ? "#0f0f1c" : "#fff", borderRadius: 16, maxWidth: 820, width: "100%", maxHeight: "85vh", overflow: "hidden", display: "flex", flexDirection: "column" }}>
            <div style={{ padding: "16px 22px", borderBottom: `1px solid ${t.border}`, display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 22 }}>📚</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 15, fontWeight: 700 }}>База знань</div>
                <div style={{ fontSize: 10, color: t.textMuted, textTransform: "uppercase", letterSpacing: 0.5 }}>Оберіть шаблон для нової задачі</div>
              </div>
              <input value={templateSearch} onChange={e => setTemplateSearch(e.target.value)} placeholder="🔍 пошук…" style={{ ...inp, fontSize: 12, padding: "6px 10px", width: 200 }} />
              <button onClick={() => setShowTemplateLib(false)} style={{ background: "transparent", border: "none", fontSize: 22, color: t.textMuted, cursor: "pointer", lineHeight: 1 }}>×</button>
            </div>
            <div style={{ padding: 18, overflowY: "auto", flex: 1 }}>
              {filteredTemplates.length === 0 ? (
                <div style={{ padding: 30, textAlign: "center", color: t.textMuted, fontSize: 12 }}>Шаблонів {templateSearch ? "не знайдено" : "ще немає — створіть у «📚 Шаблони задач»"}</div>
              ) : (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 10 }}>
                  {filteredTemplates.map(tpl => (
                    <button key={tpl.id} type="button" onClick={() => applyTemplate(tpl)}
                      style={{
                        textAlign: "left", padding: 14, borderRadius: 10, cursor: "pointer",
                        background: dark ? "rgba(255,255,255,0.03)" : "#fff",
                        border: `1px solid ${t.border}`, color: t.text, fontFamily: "inherit",
                        transition: "all .15s",
                      }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = "#6366f1"; e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 4px 16px rgba(99,102,241,0.20)"; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = t.border; e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = ""; }}
                    >
                      <div style={{ fontSize: 28, marginBottom: 6 }}>{tpl.icon || "📋"}</div>
                      <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>{tpl.name}</div>
                      {tpl.description && <div style={{ fontSize: 11, color: t.textMuted, lineHeight: 1.4 }}>{tpl.description}</div>}
                      {tpl.title_prefix && <div style={{ fontSize: 10, color: t.textMuted, marginTop: 6, fontFamily: "monospace", fontStyle: "italic" }}>"{tpl.title_prefix}…"</div>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Create-task modal (scratch mode) */}
      {creatingMode === "scratch" && (
        <div onClick={closeCreate} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 1500, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: dark ? "#0f0f1c" : "#fff", borderRadius: 16, maxWidth: 760, width: "100%", maxHeight: "92vh", overflow: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.4)" }}>
            <div style={{ padding: "16px 22px", borderBottom: `1px solid ${t.border}`, display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 22 }}>✍️</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 15, fontWeight: 700 }}>Нова задача (з нуля)</div>
                <div style={{ fontSize: 10, color: t.textMuted, textTransform: "uppercase", letterSpacing: 0.5 }}>Потрапить у колонку «Нова задача»</div>
              </div>
              <button onClick={closeCreate} style={{ background: "transparent", border: "none", fontSize: 22, color: t.textMuted, cursor: "pointer", lineHeight: 1 }}>×</button>
            </div>

            <div style={{ padding: 22, display: "flex", flexDirection: "column", gap: 14 }}>
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: t.textMuted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>Назва</div>
                <input value={newTitle} onChange={e => setNewTitle(e.target.value)} placeholder="Коротко і ясно" autoFocus style={{ ...inp, fontSize: 14, padding: "10px 12px", width: "100%", boxSizing: "border-box", fontWeight: 600 }} />
              </div>

              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: t.textMuted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>Опис</div>
                <RichEditor value={newDesc} onChange={setNewDesc} dark={dark} t={t} />
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: t.textMuted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>Виконавець</div>
                  <select value={newAssignee} onChange={e => setNewAssignee(e.target.value)} style={{ ...inp, fontSize: 12, padding: "8px 10px", width: "100%" }}>
                    <option value="">— не призначено —</option>
                    {users.map(u => <option key={u.id} value={u.id}>{u.full_name || u.username.split("@")[0]}{u.position ? ` (${u.position})` : ""}</option>)}
                  </select>
                </div>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: t.textMuted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>Пріоритет</div>
                  <select value={newPriority} onChange={e => setNewPriority(e.target.value)} style={{ ...inp, fontSize: 12, padding: "8px 10px", width: "100%" }}>
                    {Object.entries(PRIORITY_MAP).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: t.textMuted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>Посилання (Spec / ТЗ / Figma)</div>
                <input value={newSpecUrl} onChange={e => setNewSpecUrl(e.target.value)} placeholder="https://…" style={{ ...inp, fontSize: 12, padding: "8px 10px", width: "100%", boxSizing: "border-box", fontFamily: "monospace" }} />
              </div>

              <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer", padding: "10px 12px", borderRadius: 8, border: `1px solid ${newExternalDev ? "#f97316" : t.border}`, background: newExternalDev ? "rgba(249,115,22,0.08)" : "transparent" }}>
                <input type="checkbox" checked={newExternalDev} onChange={e => setNewExternalDev(e.target.checked)} style={{ width: 16, height: 16, marginTop: 1, cursor: "pointer", accentColor: "#f97316" }} />
                <span>
                  <span style={{ fontSize: 12.5, fontWeight: 700, color: newExternalDev ? "#f97316" : t.text }}>🌐 Сторонні розробники</span>
                  <span style={{ display: "block", fontSize: 10.5, color: t.textMuted, marginTop: 2, lineHeight: 1.35 }}>Задача НЕ потрапить на Dev-дошку наших розробників — залишиться лише на канбані проекту, статуси рухає SEO-фахівець сам.</span>
                </span>
              </label>
            </div>

            <div style={{ padding: "14px 22px", borderTop: `1px solid ${t.border}`, display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button onClick={closeCreate} style={{ ...btnG, fontSize: 12, padding: "8px 16px" }}>Скасувати</button>
              <button onClick={createTaskFromScratch} disabled={creatingSaving || !newTitle.trim()} style={{ ...btnP, fontSize: 12, padding: "8px 22px", opacity: !newTitle.trim() || creatingSaving ? 0.5 : 1 }}>
                {creatingSaving ? "Створюю…" : "✓ Створити задачу"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function createMenuBtn(dark, t) {
  return {
    display: "block", width: "100%", textAlign: "left",
    padding: "10px 14px", borderRadius: 6, background: "transparent",
    border: "none", color: t.text, fontSize: 12, fontWeight: 500,
    cursor: "pointer", fontFamily: "inherit",
  };
}

// Strip HTML for plain previews (Kanban card snippet etc.)
function stripHtml(html) {
  if (!html) return "";
  if (typeof DOMParser === "undefined") return String(html).replace(/<[^>]+>/g, "");
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.body.textContent || "";
}

// Lightweight rich-text editor on contentEditable. No external deps.
// Stores HTML in value/onChange. Toolbar: B / I / U / S / H2 / H3 / • / 1. / 🔗 / clear.
function RichEditor({ value, onChange, dark, t }) {
  const ref = useRef(null);
  // Init once + reset when value externally cleared (e.g. after create).
  useEffect(() => {
    if (!ref.current) return;
    if (!value && ref.current.innerHTML !== "") ref.current.innerHTML = "";
    if (value && ref.current.innerHTML === "") ref.current.innerHTML = value;
  }, [value]);
  const exec = (cmd, val) => {
    ref.current?.focus();
    document.execCommand(cmd, false, val);
    if (ref.current) onChange(ref.current.innerHTML);
  };
  const link = () => {
    const url = window.prompt("URL:");
    if (url) exec("createLink", url);
  };
  const clearFmt = () => exec("removeFormat");
  const Btn = ({ onClick, label, title }) => (
    <button
      type="button"
      onMouseDown={e => { e.preventDefault(); onClick(); }}
      title={title || label}
      style={{
        minWidth: 28, height: 28, padding: "0 8px", borderRadius: 5,
        background: dark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
        border: `1px solid ${dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
        cursor: "pointer", fontSize: 12, fontWeight: 600, color: t.text, fontFamily: "inherit",
      }}
    >{label}</button>
  );
  return (
    <div style={{ border: `1px solid ${dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.10)"}`, borderRadius: 8, overflow: "hidden", background: dark ? "rgba(255,255,255,0.02)" : "#fff" }}>
      <div style={{ display: "flex", gap: 4, padding: 6, borderBottom: `1px solid ${dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"}`, flexWrap: "wrap", background: dark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.015)" }}>
        <Btn onClick={() => exec("bold")} label={<b>B</b>} title="Жирний (Ctrl+B)" />
        <Btn onClick={() => exec("italic")} label={<i>I</i>} title="Курсив (Ctrl+I)" />
        <Btn onClick={() => exec("underline")} label={<u>U</u>} title="Підкреслено" />
        <Btn onClick={() => exec("strikeThrough")} label={<s>S</s>} title="Закреслено" />
        <span style={{ width: 1, background: dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)", margin: "2px 4px" }} />
        <Btn onClick={() => exec("formatBlock", "<h2>")} label="H2" title="Заголовок 2" />
        <Btn onClick={() => exec("formatBlock", "<h3>")} label="H3" title="Заголовок 3" />
        <Btn onClick={() => exec("formatBlock", "<p>")} label="¶" title="Звичайний текст" />
        <span style={{ width: 1, background: dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)", margin: "2px 4px" }} />
        <Btn onClick={() => exec("insertUnorderedList")} label="• —" title="Маркований список" />
        <Btn onClick={() => exec("insertOrderedList")} label="1." title="Нумерований список" />
        <Btn onClick={() => exec("formatBlock", "<blockquote>")} label="❝" title="Цитата" />
        <span style={{ width: 1, background: dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)", margin: "2px 4px" }} />
        <Btn onClick={link} label="🔗" title="Вставити посилання" />
        <Btn onClick={clearFmt} label="✕ форм." title="Очистити форматування" />
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={e => onChange(e.currentTarget.innerHTML)}
        onBlur={e => onChange(e.currentTarget.innerHTML)}
        data-placeholder="Що зробити, які кроки, посилання на скріни/доки, очікуваний результат…"
        style={{
          minHeight: 240, maxHeight: 480, padding: "14px 16px",
          fontSize: 13.5, lineHeight: 1.55, color: t.text, outline: "none",
          overflowY: "auto", fontFamily: "inherit",
        }}
      />
      <style>{`
        [contenteditable]:empty:before { content: attr(data-placeholder); color: ${t.textMuted}; pointer-events: none; }
        [contenteditable] h2 { font-size: 18px; font-weight: 700; margin: 8px 0; }
        [contenteditable] h3 { font-size: 15px; font-weight: 700; margin: 6px 0; }
        [contenteditable] ul, [contenteditable] ol { padding-left: 22px; margin: 6px 0; }
        [contenteditable] blockquote { border-left: 3px solid ${t.border}; padding: 4px 12px; color: ${t.textMuted}; margin: 6px 0; font-style: italic; }
        [contenteditable] a { color: #6366f1; text-decoration: underline; }
        [contenteditable] p { margin: 4px 0; }
      `}</style>
    </div>
  );
}
